import React, { useEffect, useState } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { makeValidator } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { FormControl, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { customTypes } from '../../extensions';
import {
  InformationExchangeApi,
  informationExchangeApiRef,
} from '../../../../apis/informationExchange';
import {
  NotificationApi,
  notificationApiRef,
} from '../../../../apis/notificationApi';

/**
 * The input props that can be specified under `ui:options` for the
 * `EntityTagsPicker` field extension.
 *
 * @public
 */
export interface EntityTagsPickerUiOptions {
  kinds?: string[];
}

/**
 * The underlying component that is rendered in the form for the `EntityTagsPicker`
 * field extension.
 *
 * @public
 */
export const EntityTagsPicker = (
  props: customTypes.FieldExtensionComponentProps<
    string[],
    EntityTagsPickerUiOptions
  >,
) => {
  const { formData, onChange, required } = props;
  const normalizedFormData = Array.isArray(formData) ? formData : [];
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState(false);
  const [tagsData, setTagsData] = useState<string[]>([]);
  const [checkDisable, setCheckDisable] = useState<any>(false);
  const [open, setOpen] = useState(false);
  const tagValidator = makeValidator().isValidTag;

  const informationExchange: InformationExchangeApi = useApi(
    informationExchangeApiRef,
  );

  const notificationApi: NotificationApi = useApi(notificationApiRef);

  useEffect(() => {
    const params = {
      page: '',
      pagesize: '',
      sortby: '',
      search: '',
    };

    informationExchange
      .getAllTags(params)
      .then((data: any) => {
        const tagsList = data?.tags?.map((val: { tag: string }) => val.tag) ?? [];
        setTagsData(tagsList);
      })
      .catch(() => {
        notificationApi.sendNotification({
          message: `Failed to load Tags`,
          disapperAfterMs: 2500,
          severity: 'error',
        });
      });
  }, []);

  const setTags = (
    _: React.ChangeEvent<{}>,
    values: Array<string | string[]> | null,
  ) => {
    // Reset error state in case all tags were removed

    let hasError = false;
    let addDuplicate = false;
    const normalizedValues = (values ?? []).flatMap(value =>
      Array.isArray(value) ? value : [value],
    );
    const currentTags = normalizedFormData;
    if (normalizedValues.length === 6) {
      setCheckDisable(true);
      return;
    } else {
      setCheckDisable(false);
    }

    // If adding a new tag
    if (normalizedValues.length && currentTags.length < normalizedValues.length) {
      const lastTagIndex = normalizedValues.length - 1;
      const newTag = (normalizedValues[lastTagIndex] = normalizedValues[
        lastTagIndex
      ]
        .toLocaleLowerCase('en-US')
        .trim());
      hasError = !tagValidator(newTag);
      addDuplicate = currentTags.indexOf(newTag) !== -1;
    }

    setInputError(hasError);
    setInputValue(!hasError ? '' : inputValue);
    if (!hasError && !addDuplicate) {
      onChange(normalizedValues);
    }
  };

  const onFocus = () => {
    setOpen(true);
  };

  // Initialize field to always return an array
  useEffectOnce(() => onChange(normalizedFormData));

  return (
    <FormControl margin="normal">
      <Autocomplete
        id="poc-tags"
        multiple
        freeSolo
        filterSelectedOptions
        getOptionDisabled={() => checkDisable}
        onChange={setTags}
        onFocus={onFocus}
        open={open}
        onClose={() => setOpen(false)}
        value={normalizedFormData}
        inputValue={inputValue}
        options={tagsData || []}
        ChipProps={{ size: 'small' }}
        renderInput={params => (
          <TextField
            {...params}
            label="Tags"
            onChange={e => {
              setInputValue(e.target.value);
              setOpen(true);
            }}
            onFocus={onFocus}
            onClick={onFocus}
            error={inputError}
            required={required}
            helperText="Add any 5 relevant tags, hit 'Enter' to add new tags. Valid format: [a-z0-9+#] separated by [-], at most 63 characters"
          />
        )}
      />
    </FormControl>
  );
};
